import { useCallback, useMemo } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { EngagementSurveyResponsesPageDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { CycleSelector } from 'modules/engagement_surveys/components/CycleSelector'
import { ParticipationCard } from 'modules/engagement_surveys/components/ParticipationCard'
import { useSurveyActions } from 'modules/engagement_surveys/hooks/useSurveyActions'
import {
  type FormFiltersStorage,
  FormResponses,
  TableColumnType,
  useFormCycleSelection,
} from 'modules/forms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export default function SurveyResponsesPage() {
  const { surveyId } = useStrictParams<'surveyId'>()
  const { data, loading, error } = useQuery(EngagementSurveyResponsesPageDocument, {
    variables: { surveyId: String(surveyId) },
    nextFetchPolicy: 'cache-first',
  })

  const survey = data?.surveys.survey
  const form = survey?.form

  const formId = form?.id.toString() || ''
  const isFormAnonymous = !!form?.anonymous

  const [savedStorage] = useLocalStorage<FormFiltersStorage | undefined>(
    LOCAL_STORAGE_KEYS.formResponseFilters(formId)
  )

  const { cycles, selectedCycleId, handleCycleChange, hasCycles } = useFormCycleSelection(
    survey,
    savedStorage ?? undefined
  )

  const materializedFormIds = useMemo(
    () => (selectedCycleId ? [selectedCycleId] : []),
    [selectedCycleId]
  )

  const cycleSelector = useMemo(() => {
    if (!hasCycles) return null

    return (
      <CycleSelector
        cycles={cycles}
        selectedCycleId={selectedCycleId}
        onCycleChange={handleCycleChange}
      />
    )
  }, [hasCycles, cycles, selectedCycleId, handleCycleChange])

  const headerContent = useCallback((filters: Record<string, unknown>) => {
    if (!form) return null

    return (
      <ParticipationCard
        formId={formId}
        materializedFormIds={materializedFormIds}
        filters={filters}
        formEndsAt={form.endsAt}
        daysToAnswer={form.configuration?.daysToAnswer}
        status={form.status}
        shouldSkip={materializedFormIds.length === 0}
      />
    )
  }, [form, formId, materializedFormIds])

  const { handleActionClick } = useSurveyActions({ formId })

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      variant="fullscreen"
      onActionClick={handleActionClick}
    >
      <FormResponses
        formId={formId}
        isFormAnonymous={isFormAnonymous}
        columns={[TableColumnType.Participant, TableColumnType.Status]}
        filters={[
          'teamIds',
          'locationIds',
          'legalEntityIds',
          'managerIds',
          'jobRoles',
          'jobTitles',
        ]}
        persistFilters
        resourceType="Surveys::Survey"
        materializedFormResourceType="Surveys::Survey"
        materializedFormIds={materializedFormIds}
        cycleSelector={cycleSelector}
        headerContent={headerContent}
      />
    </ResponsivePage.Body>
  )
}
